import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useSearchParams, useNavigate } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import { 
  Send, Bot, User, Loader2, Sparkles, 
  Image as ImageIcon, Mic, MicOff, ArrowDown, X, GitFork, ExternalLink, FolderOpen,
  MessageSquare, List, Menu, MoreVertical
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Chat = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const projectIdFromUrl = searchParams.get('project');
  
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [currentProjectId, setCurrentProjectId] = useState(projectIdFromUrl || null);
  const [currentSession, setCurrentSession] = useState(null);
  
  const [uploadedImages, setUploadedImages] = useState([]);
  const [isRecording, setIsRecording] = useState(false);
  const [showScrollButton, setShowScrollButton] = useState(false);
  const [contextTokens, setContextTokens] = useState({ used: 0, max: 1000000 });
  const [showForkDialog, setShowForkDialog] = useState(false);
  const [forkName, setForkName] = useState('');
  const [showSessionsList, setShowSessionsList] = useState(false);
  const [projectSessions, setProjectSessions] = useState([]);
  const [showSessionMenu, setShowSessionMenu] = useState(false);
  
  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const fileInputRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);

  useEffect(() => {
    if (projectIdFromUrl) {
      setCurrentProjectId(projectIdFromUrl);
      loadOrCreateProjectSession(projectIdFromUrl);
    } else {
      const generalSessionId = `chat-session-${Date.now()}`;
      setSessionId(generalSessionId);
      setCurrentSession(null);
    }
  }, [projectIdFromUrl]);

  useEffect(() => {
    if (sessionId) {
      loadChatHistory();
    }
  }, [sessionId]);

  useEffect(() => {
    scrollToBottom();
    updateContextTokens();
  }, [messages]);

  useEffect(() => {
    const container = messagesContainerRef.current;
    if (container) {
      const handleScroll = () => {
        const { scrollTop, scrollHeight, clientHeight } = container;
        setShowScrollButton(scrollHeight - scrollTop - clientHeight > 200);
      };
      container.addEventListener('scroll', handleScroll);
      return () => container.removeEventListener('scroll', handleScroll);
    }
  }, []);

  const loadOrCreateProjectSession = async (projectId) => {
    try {
      const token = localStorage.getItem('auth_token');
      const response = await axios.get(
        `${API}/projects/${projectId}/sessions/active`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      
      const session = response.data;
      setCurrentSession(session);
      setSessionId(session.session_id);
    } catch (error) {
      console.error('Error loading project session:', error);
      toast.error('Ошибка загрузки сессии проекта');
    }
  };

  const loadProjectSessions = async () => {
    if (!currentProjectId) return;
    
    try {
      const token = localStorage.getItem('auth_token');
      const response = await axios.get(
        `${API}/projects/${currentProjectId}/sessions`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      
      setProjectSessions(response.data);
      setShowSessionsList(true);
      setShowSessionMenu(false);
    } catch (error) {
      console.error('Error loading sessions:', error);
      toast.error('Ошибка загрузки сессий');
    }
  };

  const switchToSession = async (session) => {
    setCurrentSession(session);
    setSessionId(session.session_id);
    setShowSessionsList(false);
    setMessages([]);
    toast.success(`Переключено на: ${session.name}`);
  };

  const createNewSession = async () => {
    if (!currentProjectId) return;
    
    try {
      const token = localStorage.getItem('auth_token');
      const response = await axios.post(
        `${API}/projects/${currentProjectId}/sessions`,
        null,
        { 
          params: { name: `Session ${new Date().toLocaleString()}` },
          headers: { 'Authorization': `Bearer ${token}` } 
        }
      );
      
      const newSession = response.data;
      setCurrentSession(newSession);
      setSessionId(newSession.session_id);
      setMessages([]);
      setShowSessionMenu(false);
      toast.success('Новая сессия создана');
    } catch (error) {
      console.error('Error creating session:', error);
      toast.error('Ошибка создания сессии');
    }
  };

  const forkCurrentSession = async () => {
    if (!currentProjectId || !sessionId) return;
    
    try {
      const token = localStorage.getItem('auth_token');
      const response = await axios.post(
        `${API}/projects/${currentProjectId}/sessions/${sessionId}/fork`,
        null,
        { 
          params: { name: forkName || `Fork of ${currentSession?.name || 'Session'}` },
          headers: { 'Authorization': `Bearer ${token}` } 
        }
      );
      
      const forkedSession = response.data;
      setCurrentSession(forkedSession);
      setSessionId(forkedSession.session_id);
      setShowForkDialog(false);
      setForkName('');
      setShowSessionMenu(false);
      toast.success('Сессия форкнута!');
    } catch (error) {
      console.error('Error forking session:', error);
      toast.error('Ошибка форка сессии');
    }
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const updateContextTokens = () => {
    const totalChars = messages.reduce((acc, msg) => acc + msg.content.length, 0);
    const estimatedTokens = Math.ceil(totalChars / 4);
    setContextTokens({ used: estimatedTokens, max: 1000000 });
  };

  const loadChatHistory = async () => {
    if (!sessionId) return;
    
    try {
      const token = localStorage.getItem('auth_token');
      const response = await axios.get(`${API}/chat/${sessionId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setMessages(response.data);
    } catch (error) {
      console.error('Error loading chat history:', error);
    }
  };

  const handleSendMessage = async () => {
    if (!inputMessage.trim() && uploadedImages.length === 0) return;
    if (!sessionId) {
      toast.error('Сессия не инициализирована');
      return;
    }

    const token = localStorage.getItem('auth_token');
    let messageContent = inputMessage;

    if (uploadedImages.length > 0) {
      messageContent += '\n\n[Изображения загружены]';
    }

    try {
      setLoading(true);
      const endpoint = currentProjectId ? '/chat/project' : '/chat';
      
      const response = await axios.post(
        `${API}${endpoint}`,
        {
          session_id: sessionId,
          content: messageContent,
          project_id: currentProjectId
        },
        { headers: { 'Authorization': `Bearer ${token}` } }
      );

      setMessages(prev => [...prev, 
        { role: 'user', content: inputMessage, timestamp: new Date() },
        response.data
      ]);
      
      setInputMessage('');
      setUploadedImages([]);
    } catch (error) {
      console.error('Error sending message:', error);
      toast.error('Ошибка отправки сообщения');
    } finally {
      setLoading(false);
    }
  };

  const handleImageUpload = (e) => {
    const files = Array.from(e.target.files);
    setUploadedImages(prev => [...prev, ...files]);
    toast.success(`${files.length} изображений загружено`);
  };

  const removeImage = (index) => {
    setUploadedImages(prev => prev.filter((_, i) => i !== index));
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      mediaRecorderRef.current = new MediaRecorder(stream);
      audioChunksRef.current = [];

      mediaRecorderRef.current.ondataavailable = (event) => {
        audioChunksRef.current.push(event.data);
      };

      mediaRecorderRef.current.onstop = async () => {
        const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        await sendAudioToAPI(audioBlob);
        stream.getTracks().forEach(track => track.stop());
      };

      mediaRecorderRef.current.start();
      setIsRecording(true);
      toast.success('Запись началась');
    } catch (error) {
      console.error('Error starting recording:', error);
      toast.error('Ошибка доступа к микрофону');
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      toast.success('Запись остановлена');
    }
  };

  const sendAudioToAPI = async (audioBlob) => {
    try {
      const token = localStorage.getItem('auth_token');
      const formData = new FormData();
      formData.append('audio', audioBlob, 'recording.webm');

      const response = await axios.post(
        `${API}/speech-to-text`,
        formData,
        { 
          headers: { 
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          } 
        }
      );

      setInputMessage(response.data.text);
      toast.success('Текст распознан');
    } catch (error) {
      console.error('Error transcribing audio:', error);
      toast.error('Ошибка распознавания речи');
    }
  };

  return (
    <div className="flex flex-col h-full bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900">
      {/* Header - Адаптивный */}
      <div className="flex items-center justify-between p-3 md:p-4 border-b border-white/10 bg-gray-900/50 backdrop-blur">
        <div className="flex items-center gap-2 md:gap-3 flex-1 min-w-0">
          <Sparkles className="w-5 h-5 md:w-6 md:h-6 text-violet-400 flex-shrink-0" />
          <div className="min-w-0 flex-1">
            <h2 className="text-base md:text-xl font-bold text-white truncate">
              {currentProjectId ? `Проект: ${currentSession?.name || 'Загрузка...'}` : 'AI Chat'}
            </h2>
            {currentSession && (
              <p className="text-xs text-gray-400 truncate">
                {currentSession.message_count} сообщений
              </p>
            )}
          </div>
        </div>
        
        <div className="flex gap-1 md:gap-2 items-center flex-shrink-0">
          {currentProjectId && (
            <>
              {/* Мобильная версия - меню */}
              <div className="md:hidden relative">
                <Button
                  onClick={() => setShowSessionMenu(!showSessionMenu)}
                  variant="outline"
                  size="sm"
                  className="border-violet-500/30 hover:bg-violet-500/20 h-8 w-8 p-0"
                >
                  <MoreVertical className="w-4 h-4" />
                </Button>
                
                {showSessionMenu && (
                  <div className="absolute right-0 top-10 bg-gray-900 border border-white/20 rounded-lg shadow-xl z-50 w-40">
                    <button
                      onClick={loadProjectSessions}
                      className="w-full text-left px-4 py-2 hover:bg-gray-800 text-sm text-white flex items-center gap-2"
                    >
                      <List className="w-4 h-4" />
                      Сессии
                    </button>
                    <button
                      onClick={createNewSession}
                      className="w-full text-left px-4 py-2 hover:bg-gray-800 text-sm text-white flex items-center gap-2"
                    >
                      <MessageSquare className="w-4 h-4" />
                      Новая
                    </button>
                    <button
                      onClick={() => {
                        setShowForkDialog(true);
                        setShowSessionMenu(false);
                      }}
                      className="w-full text-left px-4 py-2 hover:bg-gray-800 text-sm text-white flex items-center gap-2 rounded-b-lg"
                    >
                      <GitFork className="w-4 h-4" />
                      Форк
                    </button>
                  </div>
                )}
              </div>
              
              {/* Десктопная версия - кнопки */}
              <div className="hidden md:flex gap-2">
                <Button
                  onClick={loadProjectSessions}
                  variant="outline"
                  size="sm"
                  className="border-violet-500/30 hover:bg-violet-500/20"
                >
                  <List className="w-4 h-4 mr-2" />
                  Сессии
                </Button>
                
                <Button
                  onClick={createNewSession}
                  variant="outline"
                  size="sm"
                  className="border-violet-500/30 hover:bg-violet-500/20"
                >
                  <MessageSquare className="w-4 h-4 mr-2" />
                  Новая
                </Button>
                
                <Button
                  onClick={() => setShowForkDialog(true)}
                  variant="outline"
                  size="sm"
                  className="border-violet-500/30 hover:bg-violet-500/20"
                >
                  <GitFork className="w-4 h-4 mr-2" />
                  Форк
                </Button>
                
                <Button
                  onClick={() => window.open(`https://airybrick.ru/preview/${currentProjectId}`, '_blank')}
                  variant="outline"
                  size="sm"
                  className="border-violet-500/30 hover:bg-violet-500/20"
                >
                  <ExternalLink className="w-4 h-4 mr-2" />
                  Preview
                </Button>
                
                <Button
                  onClick={() => navigate(`/files/${currentProjectId}`)}
                  variant="outline"
                  size="sm"
                  className="border-violet-500/30 hover:bg-violet-500/20"
                >
                  <FolderOpen className="w-4 h-4 mr-2" />
                  Files
                </Button>
              </div>
            </>
          )}
          
          <div className="px-2 md:px-3 py-1 bg-violet-500/20 rounded-lg">
            <span className="text-xs md:text-sm text-violet-300">
              {contextTokens.used}/{contextTokens.max}
            </span>
          </div>
        </div>
      </div>

      {/* Messages */}
      <div 
        ref={messagesContainerRef}
        className="flex-1 overflow-y-auto p-3 md:p-6 space-y-3 md:space-y-4"
      >
        {messages.map((msg, idx) => (
          <div
            key={idx}
            className={`flex gap-2 md:gap-3 ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
          >
            {msg.role === 'assistant' && (
              <div className="w-7 h-7 md:w-8 md:h-8 rounded-full bg-gradient-to-br from-violet-500 to-fuchsia-500 flex items-center justify-center flex-shrink-0">
                <Bot className="w-4 h-4 md:w-5 md:h-5 text-white" />
              </div>
            )}
            
            <div
              className={`max-w-[85%] md:max-w-[70%] rounded-2xl p-3 md:p-4 ${
                msg.role === 'user'
                  ? 'bg-gradient-to-br from-violet-600 to-fuchsia-600 text-white'
                  : 'bg-gray-800/50 text-gray-100 border border-white/10'
              }`}
            >
              <ReactMarkdown className="prose prose-invert prose-sm max-w-none text-sm md:text-base" style={{whiteSpace: "pre-wrap", wordBreak: "break-word"}}>
                {msg.content}
              </ReactMarkdown>
              
              {msg.timestamp && (
                <div className="text-xs opacity-50 mt-2">
                  {new Date(msg.timestamp).toLocaleTimeString()}
                </div>
              )}
            </div>
            
            {msg.role === 'user' && (
              <div className="w-7 h-7 md:w-8 md:h-8 rounded-full bg-gradient-to-br from-blue-500 to-cyan-500 flex items-center justify-center flex-shrink-0">
                <User className="w-4 h-4 md:w-5 md:h-5 text-white" />
              </div>
            )}
          </div>
        ))}
        
        {loading && (
          <div className="flex gap-2 md:gap-3 justify-start">
            <div className="w-7 h-7 md:w-8 md:h-8 rounded-full bg-gradient-to-br from-violet-500 to-fuchsia-500 flex items-center justify-center">
              <Loader2 className="w-4 h-4 md:w-5 md:h-5 text-white animate-spin" />
            </div>
            <div className="bg-gray-800/50 rounded-2xl p-3 md:p-4 border border-white/10">
              <div className="flex gap-2">
                <div className="w-2 h-2 bg-violet-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                <div className="w-2 h-2 bg-violet-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                <div className="w-2 h-2 bg-violet-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
              </div>
            </div>
          </div>
        )}
        
        <div ref={messagesEndRef} />
      </div>

      {/* Scroll to bottom button */}
      {showScrollButton && (
        <button
          onClick={scrollToBottom}
          className="absolute bottom-20 md:bottom-32 right-4 md:right-8 p-2 md:p-3 bg-violet-600 rounded-full shadow-lg hover:bg-violet-700 transition"
        >
          <ArrowDown className="w-4 h-4 md:w-5 md:h-5 text-white" />
        </button>
      )}

      {/* Uploaded images preview */}
      {uploadedImages.length > 0 && (
        <div className="px-3 md:px-6 py-2 flex gap-2 overflow-x-auto">
          {uploadedImages.map((img, idx) => (
            <div key={idx} className="relative">
              <img
                src={URL.createObjectURL(img)}
                alt={`upload-${idx}`}
                className="w-12 h-12 md:w-16 md:h-16 object-cover rounded-lg"
              />
              <button
                onClick={() => removeImage(idx)}
                className="absolute -top-1 -right-1 md:-top-2 md:-right-2 bg-red-500 rounded-full p-1"
              >
                <X className="w-2 h-2 md:w-3 md:h-3 text-white" />
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Input - Адаптивный */}
      <div className="p-3 md:p-4 border-t border-white/10 bg-gray-900/50 backdrop-blur">
        <div className="flex gap-1 md:gap-2 items-end">
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleImageUpload}
            accept="image/*"
            multiple
            className="hidden"
          />
          
          <Button
            onClick={() => fileInputRef.current?.click()}
            variant="outline"
            size="icon"
            className="border-violet-500/30 hover:bg-violet-500/20 h-10 w-10 md:h-11 md:w-11 flex-shrink-0"
          >
            <ImageIcon className="w-4 h-4 md:w-5 md:h-5" />
          </Button>
          
          <Button
            onClick={isRecording ? stopRecording : startRecording}
            variant="outline"
            size="icon"
            className={`border-violet-500/30 h-10 w-10 md:h-11 md:w-11 flex-shrink-0 ${
              isRecording ? 'bg-red-500/20 hover:bg-red-500/30' : 'hover:bg-violet-500/20'
            }`}
          >
            {isRecording ? <MicOff className="w-4 h-4 md:w-5 md:h-5" /> : <Mic className="w-4 h-4 md:w-5 md:h-5" />}
          </Button>
          
          <textarea
            value={inputMessage}
            onChange={(e) => setInputMessage(e.target.value)}
            onKeyPress={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                handleSendMessage();
              }
            }}
            placeholder={currentProjectId ? "Спросите о проекте..." : "Напишите сообщение..."}
            className="flex-1 bg-gray-800/50 border border-white/10 rounded-xl px-3 md:px-4 py-2 md:py-3 text-sm md:text-base text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-violet-500 resize-none"
            rows="1"
            style={{ minHeight: '40px', maxHeight: '120px' }}
          />
          
          <Button
            onClick={handleSendMessage}
            disabled={loading || (!inputMessage.trim() && uploadedImages.length === 0)}
            className="bg-gradient-to-r from-violet-600 to-fuchsia-600 hover:from-violet-700 hover:to-fuchsia-700 h-10 w-10 md:h-11 md:w-11 p-0 flex-shrink-0"
          >
            <Send className="w-4 h-4 md:w-5 md:h-5" />
          </Button>
        </div>
      </div>

      {/* Fork Dialog */}
      {showForkDialog && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-900 border border-white/20 rounded-2xl p-4 md:p-6 w-full max-w-md">
            <h3 className="text-lg md:text-xl font-bold text-white mb-3 md:mb-4">Форк сессии</h3>
            <input
              type="text"
              value={forkName}
              onChange={(e) => setForkName(e.target.value)}
              placeholder="Название новой сессии"
              className="w-full bg-gray-800 border border-white/10 rounded-lg px-3 md:px-4 py-2 text-sm md:text-base text-white mb-3 md:mb-4"
            />
            <div className="flex gap-2">
              <Button
                onClick={forkCurrentSession}
                className="flex-1 bg-gradient-to-r from-violet-600 to-fuchsia-600 text-sm md:text-base"
              >
                Создать форк
              </Button>
              <Button
                onClick={() => {
                  setShowForkDialog(false);
                  setForkName('');
                }}
                variant="outline"
                className="flex-1 text-sm md:text-base"
              >
                Отмена
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Sessions List Dialog */}
      {showSessionsList && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-900 border border-white/20 rounded-2xl p-4 md:p-6 w-full max-w-2xl max-h-[80vh] overflow-y-auto">
            <h3 className="text-lg md:text-xl font-bold text-white mb-3 md:mb-4">Сессии проекта</h3>
            <div className="space-y-2">
              {projectSessions.map((session) => (
                <div
                  key={session.id}
                  onClick={() => switchToSession(session)}
                  className={`p-3 md:p-4 rounded-lg border cursor-pointer transition ${
                    session.session_id === sessionId
                      ? 'border-violet-500 bg-violet-500/20'
                      : 'border-white/10 hover:bg-gray-800/50'
                  }`}
                >
                  <div className="flex justify-between items-start">
                    <div className="min-w-0 flex-1">
                      <h4 className="text-sm md:text-base text-white font-semibold truncate">{session.name}</h4>
                      <p className="text-xs md:text-sm text-gray-400">
                        {session.message_count} сообщений
                      </p>
                    </div>
                    <div className="text-xs text-gray-500 ml-2">
                      {new Date(session.updated_at).toLocaleString('ru', { 
                        day: '2-digit', 
                        month: '2-digit',
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </div>
                  </div>
                  {session.parent_session_id && (
                    <div className="mt-2 text-xs text-violet-400 flex items-center gap-1">
                      <GitFork className="w-3 h-3" />
                      Форк
                    </div>
                  )}
                </div>
              ))}
            </div>
            <Button
              onClick={() => setShowSessionsList(false)}
              variant="outline"
              className="w-full mt-3 md:mt-4"
            >
              Закрыть
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Chat;
